/**
 * ExecutiveValueDashboard
 *
 * Strategic dashboard for executive stakeholders showing catalog health,
 * content pipeline progress, demand-supply gaps, and value quantification.
 *
 * Part of Story 73: Executive Value Dashboard
 */

import { useState, useEffect } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle2,
  FileText,
  DollarSign,
  Bot,
  Layers,
  Target,
  Settings,
  Download
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter
} from 'recharts';
import { BotAttentionSection } from '@/components/shared/BotAttentionSection';
import type {
  ExecutiveDashboardData,
  ValueConfig
} from '@/services/executiveDashboardService';
import {
  fetchDashboard,
  fetchValueConfig,
  updateValueConfig
} from '@/services/executiveDashboardService';

const COLORS = ['#3b82f6', '#22c55e', '#f97316', '#ef4444', '#8b5cf6'];

export default function ExecutiveValueDashboard() {
  const { selectedSite } = useSite();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ExecutiveDashboardData | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [configOpen, setConfigOpen] = useState(false);
  const [configForm, setConfigForm] = useState<ValueConfig>({
    aov: 100,
    conversionRate: 0.025,
    monthlyTraffic: 10000,
    industryVertical: 'ecommerce'
  });

  useEffect(() => {
    if (selectedSite) {
      loadData();
    }
  }, [selectedSite]);

  const loadData = async () => {
    if (!selectedSite) return;
    setLoading(true);
    try {
      const dashboardData = await fetchDashboard(selectedSite.id);
      setData(dashboardData);

      // Load config for form
      const config = await fetchValueConfig(selectedSite.id);
      setConfigForm(config);
    } catch (error) {
      console.error('Failed to load executive dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!selectedSite) return;
    try {
      await updateValueConfig(selectedSite.id, configForm);
      setConfigOpen(false);
      loadData(); // Reload with new config
    } catch (error) {
      console.error('Failed to save config:', error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value: number) => `${value}%`;

  if (!selectedSite) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please select a site to view the executive dashboard.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load dashboard data. Please try again.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const { hero, catalogHealth, contentPipeline, robotObservability, catalogDiversity, demandSupplyGap, valueQuantification, botAttentionPriority } = data;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Executive Value Dashboard</h1>
          <p className="text-muted-foreground">
            Strategic overview for {data.meta.siteName}
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={configOpen} onOpenChange={setConfigOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Settings className="h-4 w-4 mr-2" />
                Configure
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Value Configuration</DialogTitle>
                <DialogDescription>
                  Configure parameters for value quantification
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Average Order Value ($)</Label>
                  <Input
                    type="number"
                    value={configForm.aov}
                    onChange={e => setConfigForm({ ...configForm, aov: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Conversion Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={(configForm.conversionRate * 100).toFixed(1)}
                    onChange={e => setConfigForm({ ...configForm, conversionRate: parseFloat(e.target.value) / 100 || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Monthly Traffic</Label>
                  <Input
                    type="number"
                    value={configForm.monthlyTraffic}
                    onChange={e => setConfigForm({ ...configForm, monthlyTraffic: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfigOpen(false)}>Cancel</Button>
                <Button onClick={handleSaveConfig}>Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Hero KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completeness Score</p>
                <p className="text-3xl font-bold">{hero.completenessScore}%</p>
              </div>
              <div className={`flex items-center ${hero.trends.completeness >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {hero.trends.completeness >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                <span className="text-sm ml-1">{hero.trends.completeness >= 0 ? '+' : ''}{hero.trends.completeness.toFixed(1)}%</span>
              </div>
            </div>
            <Progress value={hero.completenessScore} className="mt-3" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Content Published</p>
                <p className="text-3xl font-bold">{hero.contentPublished.toLocaleString()}</p>
              </div>
              <FileText className="h-8 w-8 text-muted-foreground" />
            </div>
            <div className={`flex items-center mt-2 ${hero.trends.content >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {hero.trends.content >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
              <span className="text-sm ml-1">{hero.trends.content >= 0 ? '+' : ''}{hero.trends.content.toFixed(1)}% vs prior period</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Demand Coverage</p>
                <p className="text-3xl font-bold">{hero.demandCoverage}%</p>
              </div>
              <Target className="h-8 w-8 text-muted-foreground" />
            </div>
            <Progress value={hero.demandCoverage} className="mt-3" />
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Est. Monthly Value</p>
                <p className="text-3xl font-bold text-green-600">{formatCurrency(hero.estimatedMonthlyValue)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-600" />
            </div>
            <div className={`flex items-center mt-2 ${hero.trends.value >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {hero.trends.value >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
              <span className="text-sm ml-1">{hero.trends.value >= 0 ? '+' : ''}{hero.trends.value.toFixed(1)}% potential</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-8">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="health">Catalog Health</TabsTrigger>
          <TabsTrigger value="diversity">Diversity</TabsTrigger>
          <TabsTrigger value="demand">Demand-Supply</TabsTrigger>
          <TabsTrigger value="content">Content Pipeline</TabsTrigger>
          <TabsTrigger value="robot">Robot Health</TabsTrigger>
          <TabsTrigger value="bot">Bot Priority</TabsTrigger>
          <TabsTrigger value="value">Value</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Content Pipeline Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Content Pipeline</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Published', value: contentPipeline.byStatus.published },
                          { name: 'Validated', value: contentPipeline.byStatus.validated },
                          { name: 'Draft', value: contentPipeline.byStatus.draft },
                          { name: 'Needs Review', value: contentPipeline.byStatus.needsReview },
                          { name: 'Needs Research', value: contentPipeline.byStatus.needsResearch }
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {COLORS.map((color, index) => (
                          <Cell key={`cell-${index}`} fill={color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-center mt-2">
                  <span className="text-2xl font-bold">{contentPipeline.totalGenerated.toLocaleString()}</span>
                  <span className="text-muted-foreground ml-2">total pages generated</span>
                </div>
              </CardContent>
            </Card>

            {/* Value Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Value Breakdown</CardTitle>
                <CardDescription>Monthly opportunity by category</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        { name: 'Content', value: valueQuantification.breakdown.contentCompleteness },
                        { name: 'SEO', value: valueQuantification.breakdown.seoTraffic },
                        { name: 'Demand', value: valueQuantification.breakdown.demandGaps },
                        { name: 'Robot', value: valueQuantification.breakdown.robotHealth }
                      ]}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={formatCurrency} />
                      <YAxis type="category" dataKey="name" width={80} />
                      <Tooltip formatter={(value) => formatCurrency(value as number)} />
                      <Bar dataKey="value" fill="#22c55e" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-center mt-4">
                  <span className="text-3xl font-bold text-green-600">
                    {formatCurrency(valueQuantification.estimatedMonthlyValue)}
                  </span>
                  <span className="text-muted-foreground ml-2">total monthly opportunity</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Opportunity Ladder */}
          <Card>
            <CardHeader>
              <CardTitle>Opportunity Ladder</CardTitle>
              <CardDescription>Prioritized actions by value and effort</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Action</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Potential Value</TableHead>
                    <TableHead>Effort</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {valueQuantification.opportunityLadder.map((opp, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{opp.description}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{opp.category.replace(/_/g, ' ')}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-green-600">
                        {formatCurrency(opp.potentialValue)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={opp.effort === 'low' ? 'default' : opp.effort === 'medium' ? 'secondary' : 'destructive'}
                        >
                          {opp.effort}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Catalog Health Tab */}
        <TabsContent value="health" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Total Products</p>
                  <p className="text-3xl font-bold">{catalogHealth.totalProducts.toLocaleString()}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Products with Issues</p>
                  <p className="text-3xl font-bold text-orange-600">{catalogHealth.productsWithIssues.toLocaleString()}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Avg Completeness</p>
                  <p className="text-3xl font-bold">{catalogHealth.completenessScore}%</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Issues by Severity */}
            <Card>
              <CardHeader>
                <CardTitle>Issues by Severity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      Critical
                    </span>
                    <span className="font-mono">{catalogHealth.issuesBySeverity.critical}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-orange-500" />
                      High
                    </span>
                    <span className="font-mono">{catalogHealth.issuesBySeverity.high}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      Medium
                    </span>
                    <span className="font-mono">{catalogHealth.issuesBySeverity.medium}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gray-500" />
                      Low
                    </span>
                    <span className="font-mono">{catalogHealth.issuesBySeverity.low}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Attribute Coverage */}
            <Card>
              <CardHeader>
                <CardTitle>Critical Attribute Coverage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {catalogHealth.criticalAttributesCoverage.map(attr => {
                    const total = attr.found + attr.missing + attr.partial;
                    const coveragePct = total > 0 ? (attr.found / total) * 100 : 0;
                    return (
                      <div key={attr.attribute} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="capitalize">{attr.attribute}</span>
                          <span>{coveragePct.toFixed(0)}%</span>
                        </div>
                        <Progress value={coveragePct} />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Diversity Tab */}
        <TabsContent value="diversity" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Diversity Score</p>
                <p className="text-3xl font-bold">{catalogDiversity.diversityScore}/100</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Categories</p>
                <p className="text-3xl font-bold">{catalogDiversity.categoryCount}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Brands</p>
                <p className="text-3xl font-bold">{catalogDiversity.brandCount}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Total SKUs</p>
                <p className="text-3xl font-bold">{catalogDiversity.totalSkus.toLocaleString()}</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Brand Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Top Brands</CardTitle>
                <CardDescription>Concentration: {catalogDiversity.brandConcentration.concentrationIndex.toFixed(1)}%</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={catalogDiversity.brandConcentration.topBrands}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#3b82f6" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Price Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Price Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={catalogDiversity.priceDistribution}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        dataKey="count"
                        nameKey="tier"
                        label={({ tier, percentage }) => `${tier} ${percentage.toFixed(0)}%`}
                      >
                        {catalogDiversity.priceDistribution.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Demand-Supply Tab */}
        <TabsContent value="demand" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Demand Coverage</p>
                <p className="text-3xl font-bold">{demandSupplyGap.demandCoveragePercent}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Total Market Demand</p>
                <p className="text-3xl font-bold">{demandSupplyGap.totalMarketDemand.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Covered Demand</p>
                <p className="text-3xl font-bold">{demandSupplyGap.coveredDemand.toLocaleString()}</p>
              </CardContent>
            </Card>
          </div>

          {/* Quadrant Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-green-50 dark:bg-green-950/20">
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Stars (High/High)</p>
                <p className="text-2xl font-bold text-green-600">{demandSupplyGap.quadrantSummary.highDemandHighCoverage}</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 dark:bg-red-950/20">
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Opportunities (High/Low)</p>
                <p className="text-2xl font-bold text-red-600">{demandSupplyGap.quadrantSummary.highDemandLowCoverage}</p>
              </CardContent>
            </Card>
            <Card className="bg-yellow-50 dark:bg-yellow-950/20">
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Over-Invested (Low/High)</p>
                <p className="text-2xl font-bold text-yellow-600">{demandSupplyGap.quadrantSummary.lowDemandHighCoverage}</p>
              </CardContent>
            </Card>
            <Card className="bg-gray-50 dark:bg-gray-950/20">
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Long-Tail (Low/Low)</p>
                <p className="text-2xl font-bold text-gray-600">{demandSupplyGap.quadrantSummary.lowDemandLowCoverage}</p>
              </CardContent>
            </Card>
          </div>

          {/* Top Opportunities */}
          <Card>
            <CardHeader>
              <CardTitle>Top Opportunities</CardTitle>
              <CardDescription>Categories with high demand and low coverage</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Monthly Searches</TableHead>
                    <TableHead className="text-right">Est. Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {demandSupplyGap.topOpportunities.map((opp, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{opp.category}</TableCell>
                      <TableCell className="text-right font-mono">{opp.monthlySearches.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-mono text-green-600">{formatCurrency(opp.estimatedValue)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Content Pipeline Tab */}
        <TabsContent value="content" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Status Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Content by Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Published', value: contentPipeline.byStatus.published, color: '#22c55e' },
                          { name: 'Validated', value: contentPipeline.byStatus.validated, color: '#3b82f6' },
                          { name: 'Draft', value: contentPipeline.byStatus.draft, color: '#f97316' },
                          { name: 'Needs Review', value: contentPipeline.byStatus.needsReview, color: '#eab308' },
                          { name: 'Needs Research', value: contentPipeline.byStatus.needsResearch, color: '#ef4444' }
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        dataKey="value"
                        label
                      >
                        {[0, 1, 2, 3, 4].map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Weekly Trend */}
            <Card>
              <CardHeader>
                <CardTitle>Weekly Production</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={contentPipeline.weeklyTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="generated" name="Generated" stackId="1" stroke="#3b82f6" fill="#3b82f6" />
                      <Area type="monotone" dataKey="published" name="Published" stackId="2" stroke="#22c55e" fill="#22c55e" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Deployment Rate</p>
                <p className="text-3xl font-bold">{contentPipeline.deploymentRate}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Avg Time to Publish</p>
                <p className="text-3xl font-bold">{contentPipeline.avgTimeToPublish.toFixed(1)}h</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Total Generated</p>
                <p className="text-3xl font-bold">{contentPipeline.totalGenerated.toLocaleString()}</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Robot Health Tab */}
        <TabsContent value="robot" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Health Score</p>
                <p className="text-3xl font-bold">{robotObservability.healthScore}/100</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Bot Requests (30d)</p>
                <p className="text-3xl font-bold">{robotObservability.totalBotRequests.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Visit Rate</p>
                <p className="text-3xl font-bold">{robotObservability.visibility.visitRate}%</p>
              </CardContent>
            </Card>
          </div>

          {/* Bot Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Bot Traffic Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={robotObservability.botBreakdown.slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="botType" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="requests" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bot Priority Tab */}
        <TabsContent value="bot">
          <BotAttentionSection
            priorityItems={botAttentionPriority.priorityItems}
            summary={botAttentionPriority.summary}
            trendingCategories={botAttentionPriority.trendingCategories}
            aiAssistantFocus={botAttentionPriority.aiAssistantFocus}
            discoveryProblems={botAttentionPriority.discoveryProblems}
            variant="executive"
            onViewDetails={(item) => {
              // Navigate to product details
              console.log('View details:', item);
            }}
          />
        </TabsContent>

        {/* Value Tab */}
        <TabsContent value="value" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Value Quantification</CardTitle>
              <CardDescription>
                Based on AOV: {formatCurrency(valueQuantification.configuredParams.aov)},
                CVR: {(valueQuantification.configuredParams.conversionRate * 100).toFixed(1)}%,
                Traffic: {valueQuantification.configuredParams.monthlyTraffic.toLocaleString()}/mo
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Value Breakdown Chart */}
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        { name: 'Content Completeness', value: valueQuantification.breakdown.contentCompleteness },
                        { name: 'SEO Traffic', value: valueQuantification.breakdown.seoTraffic },
                        { name: 'Demand Gaps', value: valueQuantification.breakdown.demandGaps },
                        { name: 'Robot Health', value: valueQuantification.breakdown.robotHealth }
                      ]}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                      <YAxis tickFormatter={formatCurrency} />
                      <Tooltip formatter={(value) => formatCurrency(value as number)} />
                      <Bar dataKey="value" fill="#22c55e" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Total Value */}
                <div className="flex flex-col items-center justify-center">
                  <p className="text-muted-foreground">Total Estimated Monthly Value</p>
                  <p className="text-5xl font-bold text-green-600 mt-4">
                    {formatCurrency(valueQuantification.estimatedMonthlyValue)}
                  </p>
                  <p className="text-muted-foreground mt-2">per month</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Opportunity Ladder */}
          <Card>
            <CardHeader>
              <CardTitle>Action Priority Ladder</CardTitle>
              <CardDescription>Sorted by value-to-effort ratio</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Potential Value</TableHead>
                    <TableHead>Effort</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {valueQuantification.opportunityLadder.map((opp, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-bold">{idx + 1}</TableCell>
                      <TableCell>{opp.description}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{opp.category.replace(/_/g, ' ')}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-green-600">
                        {formatCurrency(opp.potentialValue)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={opp.effort === 'low' ? 'default' : opp.effort === 'medium' ? 'secondary' : 'destructive'}
                        >
                          {opp.effort}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
